import { Link } from 'react-router-dom';
import { SERVICE_MENU } from '@/lib/constants';
import { MapPin, Phone, Mail } from 'lucide-react';

export default function Footer() {
  return (
    <footer className="bg-[#09090B]" data-testid="site-footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">

          {/* Brand + Address */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block mb-5" data-testid="footer-logo">
              <img src="/assets/img/brand/logo-light.png" alt="AmericanTrademark Firm" className="h-9 w-auto" />
            </Link>
            <p className="text-sm text-zinc-400 font-body leading-relaxed mb-6 max-w-xs">
              Professional trademark registration, copyright protection, and corporate services trusted by businesses across the USA.
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#002FA7] mt-0.5 shrink-0" />
                <span className="text-sm text-zinc-400 font-body">
                  3101 Park Blvd, 1st Floor,<br />Palo Alto, CA 94306
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#002FA7] shrink-0" />
                <span className="text-sm text-zinc-400 font-body">+1 (213) 646-7133</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#002FA7] shrink-0" />
                <span className="text-sm text-zinc-400 font-body">info@americantrademarkfirm.com</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h4 className="font-heading font-bold text-xs uppercase tracking-[0.15em] text-white mb-4">
              Company
            </h4>
            <ul className="space-y-2.5">
              {[
                { to: '/', label: 'Home' },
                { to: '/about', label: 'About Us' },
                { to: '/contact', label: 'Contact Us' },
                { to: '/privacy-policy', label: 'Privacy Policy' },
                { to: '/terms-of-service', label: 'Terms of Service' },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-zinc-400 hover:text-white transition-colors font-body"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Trademark Services */}
          <div className="lg:col-span-3">
            <h4 className="font-heading font-bold text-xs uppercase tracking-[0.15em] text-white mb-4">
              Trademark Services
            </h4>
            <ul className="space-y-2.5">
              {SERVICE_MENU[0].services.map((svc) => (
                <li key={svc.slug}>
                  <Link
                    to={`/services/${svc.slug}`}
                    className="text-sm text-zinc-400 hover:text-white transition-colors font-body"
                    data-testid={`footer-svc-${svc.slug}`}
                  >
                    {svc.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Other Services */}
          <div className="lg:col-span-3">
            <h4 className="font-heading font-bold text-xs uppercase tracking-[0.15em] text-white mb-4">
              More Services
            </h4>
            <ul className="space-y-2.5">
              {SERVICE_MENU.slice(1, 5).map((cat) => (
                <li key={cat.services[0].slug}>
                  <Link
                    to={`/services/${cat.services[0].slug}`}
                    className="text-sm text-zinc-400 hover:text-white transition-colors font-body"
                  >
                    {cat.category}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/services/amazon-brand-registry"
                  className="text-sm text-zinc-400 hover:text-white transition-colors font-body"
                >
                  Amazon Brand Registry
                </Link>
              </li>
              <li>
                <Link
                  to="/services/business-formation-services"
                  className="text-sm text-zinc-400 hover:text-white transition-colors font-body"
                >
                  Business Formation
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-zinc-500 font-body" data-testid="footer-copyright">
            &copy; 2026 AmericanTrademark Firm. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs" data-testid="footer-legal-links">
            <Link
              to="/privacy-policy"
              className="text-zinc-500 hover:text-white transition-colors font-body"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </Link>
            <span className="text-zinc-700">|</span>
            <Link
              to="/terms-of-service"
              className="text-zinc-500 hover:text-white transition-colors font-body"
              data-testid="footer-terms-link"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
